import React, { useRef, useState, useEffect } from 'react';
import './Camera.css';

const Camera = ({ onCapture, onClose, scanBarcode = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [error, setError] = useState('');
  const [facingMode, setFacingMode] = useState('environment'); // default to back camera

  // Open camera on component mount
  useEffect(() => {
    openCamera();
    
    // Clean up on unmount
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [facingMode]);

  const openCamera = async () => {
    try {
      setError('');
      
      // Get access to the camera
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraOpen(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      
      // Provide user-friendly error message
      let errorMessage = 'Could not access camera. ';
      
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera device found.';
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += 'Camera access was denied. Please allow camera access and try again.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += 'Camera is already in use by another application.';
      } else {
        errorMessage += 'Please check your camera settings and try again.';
      }
      
      setError(errorMessage);
      setIsCameraOpen(false);
    }
  };

  const switchCamera = () => {
    // First stop the current stream
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
    
    // Then switch the facing mode
    setFacingMode(prevMode => prevMode === 'environment' ? 'user' : 'environment');
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the current video frame to the canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert the canvas to a data URL
    const imageDataURL = canvas.toDataURL('image/jpeg');
    
    // Pass the captured image data to the parent component
    onCapture(imageDataURL);
  };

  return (
    <div className="camera-container">
      {error && (
        <div className="camera-error">
          <p>{error}</p>
          <button className="camera-button retry" onClick={openCamera}>Retry</button>
          <button className="camera-button close" onClick={onClose}>Close</button>
        </div>
      )}
      
      {!error && (
        <>
          <div className="video-container">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              onCanPlay={() => setIsCameraOpen(true)}
            />
            
            {scanBarcode && (
              <div className="barcode-scanner-overlay">
                <div className="scanner-rectangle"></div>
                <p className="scanner-message">Position barcode within the rectangle</p>
              </div>
            )}
          </div>
          
          <div className="camera-controls">
            <button 
              className="camera-button switch" 
              onClick={switchCamera}
              title="Switch Camera"
            >
              <i className="fas fa-sync"></i>
            </button>
            
            {!scanBarcode && (
              <button 
                className="camera-button capture" 
                onClick={captureImage}
                disabled={!isCameraOpen}
                title="Take Photo"
              >
                <i className="fas fa-camera"></i>
              </button>
            )}
            
            <button 
              className="camera-button close" 
              onClick={onClose}
              title="Close Camera"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          {/* Hidden canvas for capturing images */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </>
      )}
    </div>
  );
};

export default Camera;